import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type DependencyList,
  type ReactNode,
  type RefObject,
} from 'react';
import type { Page } from '../api/query';

// Data and state hooks shared by the consoles: live reloads, keyset paging, URL and browser-storage state, sizes.

// ---------------------------------------------------------------------------------------------------- live data

/** One event of the app's live stream; only its `type` is read here. */
export interface LiveTopicEvent {
  type: string;
}
/** Subscribes to the app's live stream; returns the unsubscribe function. */
export type LiveSubscribe = (fn: (e: LiveTopicEvent) => void) => () => void;

const LiveContext = createContext<LiveSubscribe | null>(null);

/** Hands the app's live stream (`openLiveStream` fan-out) to `useLive`, `usePaged` and `useChangeSignal`. */
export function LiveProvider({ subscribe, children }: { subscribe: LiveSubscribe; children: ReactNode }) {
  return createElement(LiveContext.Provider, { value: subscribe }, children);
}

/** `fn` after `wait` ms of quiet, but at least every `maxWait` ms while triggers keep coming. */
export function debounceWithMaxWait(fn: () => void, wait = 300, maxWait = 2000) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let since = 0;
  const run = () => {
    timer = undefined;
    fn();
  };
  return {
    trigger() {
      const now = Date.now();
      if (timer === undefined) since = now;
      else clearTimeout(timer);
      timer = setTimeout(run, Math.max(0, Math.min(wait, since + maxWait - now)));
    },
    cancel() {
      clearTimeout(timer);
      timer = undefined;
    },
  };
}

const matches = (prefixes: string[], type: string) => prefixes.some((p) => type.startsWith(p));

/**
 * Loads data and reloads it when a live event whose type starts with one of `refetchOn` arrives (under
 * `LiveProvider`; debounced so a burst causes one request, at least every 2 s while events keep coming). A late
 * answer of an older load never overwrites a newer one.
 */
export function useLive<T>(load: () => Promise<T>, deps: DependencyList, refetchOn: string[] = []) {
  const subscribe = useContext(LiveContext);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    try {
      const d = await loadRef.current();
      if (n === seq.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (n === seq.current) setError(e);
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const topics = refetchOn.join('|');
  useEffect(() => {
    if (!topics || !subscribe) return;
    const prefixes = topics.split('|');
    const refetch = debounceWithMaxWait(() => void reload(), 300, 2000);
    const off = subscribe((e) => {
      if (matches(prefixes, e.type)) refetch.trigger();
    });
    return () => {
      refetch.cancel();
      off();
    };
  }, [topics, subscribe, reload]);

  return { data, error, loading, reload, setData };
}

/**
 * Live changes for a big list: counts the events of `topics` since the last `reset`, so a grid can offer
 * "n changes · refresh" instead of reloading under the user's scroll position. `ignore` drops event types that do
 * not change the rows.
 */
export function useChangeSignal(topics: string[], ignore?: (type: string) => boolean) {
  const subscribe = useContext(LiveContext);
  const [changes, setChanges] = useState(0);
  const ignoreRef = useRef(ignore);
  ignoreRef.current = ignore;
  const key = topics.join('|');
  useEffect(() => {
    const prefixes = key.split('|').filter(Boolean);
    if (!prefixes.length || !subscribe) return;
    return subscribe((e) => {
      if (matches(prefixes, e.type) && !ignoreRef.current?.(e.type)) setChanges((n) => n + 1);
    });
  }, [key, subscribe]);
  return { changes, reset: useCallback(() => setChanges(0), []) };
}

/**
 * A keyset-paged list: the first page from `load` (reloaded with `deps` and on `refetchOn` events, like `useLive`)
 * plus the pages fetched with `loadMore` (`nextPageToken`). Filters belong in `load`, never on the loaded items.
 * Pairs with `LoadMore`.
 */
export function usePaged<T>(
  load: (pageToken?: string) => Promise<Page<T>>,
  deps: DependencyList,
  refetchOn: string[] = []
) {
  const first = useLive(() => load(), deps, refetchOn);
  const [extra, setExtra] = useState<{ items: T[]; token: string | null } | null>(null);
  const [moreError, setMoreError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    setExtra(null);
    setMoreError(null);
  }, [first.data]);
  const next = extra ? extra.token : (first.data?.nextPageToken ?? null);
  const loadMore = async () => {
    if (!next || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const r = await loadRef.current(next);
      setExtra((x) => ({ items: [...(x?.items ?? []), ...r.items], token: r.nextPageToken ?? null }));
    } catch (e) {
      setMoreError(e);
    } finally {
      setLoadingMore(false);
    }
  };
  const items = [...(first.data?.items ?? []), ...(extra?.items ?? [])];
  return { ...first, items, next, loadMore, moreError, loadingMore };
}

// ---------------------------------------------------------------------------------------------------- URL state

/** react-router's `useSearchParams` (any router with the same shape): the kit needs no router of its own. */
export type UseSearchParams = () => readonly [
  URLSearchParams,
  (next: (cur: URLSearchParams) => URLSearchParams, options?: { replace?: boolean }) => void,
];

/**
 * `useParam(key, fallback)` over the app's router: one URL query parameter as state (filters and tabs stay
 * shareable); setting the fallback removes it, and several setters called in one handler all apply.
 *
 *   export const useParam = createUseParam(useSearchParams);
 */
export function createUseParam(useSearchParams: UseSearchParams) {
  // A functional setter gets the params of the last render, so two setters in a row (tenant, then reset server)
  // would drop the first; updates in the same tick build on each other through this.
  let pending: URLSearchParams | null = null;
  return function useParam<T extends string = string>(
    key: string,
    fallback: NoInfer<T>
  ): [T, (v: T) => void] {
    const [params, setParams] = useSearchParams();
    const value = (params.get(key) as T | null) ?? fallback;
    const set = useCallback(
      (v: T) =>
        setParams(
          (cur) => {
            const next = new URLSearchParams(pending ?? cur);
            if (v === fallback || v === '') next.delete(key);
            else next.set(key, v);
            if (!pending) queueMicrotask(() => (pending = null));
            pending = next;
            return next;
          },
          { replace: true }
        ),
      [key, fallback, setParams]
    );
    return [value, set];
  };
}

/**
 * A one-shot parameter (`?open=<id>` from a link elsewhere): read once, then dropped from the URL without a
 * navigation so the page's own URL state stays as it is.
 */
export function useOpenParam(name = 'open') {
  const [value, setValue] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get(name)
  );
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(name)) return;
    params.delete(name);
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
  }, [name]);
  return [value, setValue] as const;
}

// ---------------------------------------------------------------------------------------------------- storage

/**
 * State kept in localStorage under `key` (a per-browser preference: grid size, a folded panel). `validate` drops a
 * stored value of another shape; blocked storage just keeps the value for this page.
 */
export function useLocalStorageState<T>(key: string, fallback: T, validate?: (v: unknown) => v is T) {
  const read = (): T => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return fallback;
      const v: unknown = JSON.parse(raw);
      return validate && !validate(v) ? fallback : (v as T);
    } catch {
      return fallback;
    }
  };
  const [value, setValue] = useState<T>(read);
  const set = useCallback(
    (next: T | ((cur: T) => T)) =>
      setValue((cur) => {
        const v = typeof next === 'function' ? (next as (cur: T) => T)(cur) : next;
        try {
          localStorage.setItem(key, JSON.stringify(v));
        } catch {
          /* storage blocked: kept for this page only */
        }
        return v;
      }),
    [key]
  );
  return [value, set] as const;
}

// ---------------------------------------------------------------------------------------------------- size

/** An element's content box size, following resizes (ResizeObserver; 0 × 0 until measured). */
export function useElementSize<T extends HTMLElement>(ref: RefObject<T | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setSize({ width: el.clientWidth, height: el.clientHeight });
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) =>
      setSize({ width: Math.round(e.contentRect.width), height: Math.round(e.contentRect.height) })
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}
